import React from "react";
import { DispatchProp } from "react-redux";
import { remove, isEmpty, max, cloneDeep } from "../../utils/lodash";
import { QuestionModel } from "../../models/QuestionModel";
import { ControlProps, BaseControl } from "./BaseControl";
import { ChoiceModel } from "../../models/ChoiceModel";
import { isNullOrEmpty } from "../../utils/index";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { getSettingNumber, getSettingString } from "../../models/utilities/settingUtilities";
import { getRendererProps } from "../renderers/BaseRenderer";
import { translate } from "../../models/utilities/translationUtilities";
import { NumericRankingModel } from "../../models/NumericRankingModel";
import { MentionModel } from "../../models/MentionModel";
import NumericRankingRenderer from "../renderers/v2/NumericRankingRenderer";
import { getSoftPromptCode } from "../../models/utilities/questionUtilities";
import { getChoice } from "../../models/utilities/variableUtilities";
import { isVisible } from "../../models/utilities/choiceUtilities";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";
import { VariableModel } from "../../models/VariableModel";
import { getQuestionTitleAndLabelAriaLabelledByIds } from "../../utils/WCAGHelper";

export const getNumericRankingQuestionAriaDescribedBy = getQuestionTitleAndLabelAriaLabelledByIds;

interface Props extends ControlProps, DispatchProp {}

interface State {
    rankings: NumericRankingModel[];
}

export class NumericRankingControl extends BaseControl<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { rankings: [] };
    }

    initializeQuestion(question: QuestionModel, props: Props) {
        super.initializeQuestion(question, props);
        const mentions: MentionModel[] = this.getMentions(props);
        this.setState({
            rankings: mentions.map((mention, i) => ({
                choiceValue: mention.value,
                rankingValue: i + 1,
            })),
        });
    }

    getSoftPromptChoiceRanking = (choiceValue: string): NumericRankingModel => ({
        choiceValue,
        rankingValue: 1,
    });

    getMaxAnswers(): number {
        const maxAnswers = getSettingNumber(this.props.question, "MaxAllowedAnswers");
        const choicesCount = this.props.sequencedVariables[0].choiceList.filter(
            (a) => !isNullOrEmpty(a.value),
        ).length;

        return maxAnswers !== undefined && !Number.isNaN(maxAnswers)
            ? Math.min(maxAnswers, choicesCount)
            : choicesCount;
    }

    getMinAnswers(): number {
        const minAnswers = getSettingNumber(this.props.question, "MinRequiredAnswers");
        if (minAnswers !== undefined && !Number.isNaN(minAnswers)) {
            return minAnswers;
        }
        return 1;
    }

    getRankingValue = (value: string): number => {
        const sanitizedValue = value.replace(/\D/g, "");
        if (!isNullOrEmpty(sanitizedValue)) {
            const maxAnswers = this.getMaxAnswers();
            const intValue = parseInt(sanitizedValue, 10);
            if (!Number.isNaN(intValue) && intValue >= 1 && intValue <= maxAnswers) {
                return intValue;
            }
        }
        return undefined;
    };

    onChange = (choice: ChoiceModel, value: string) => {
        const { sequencedVariables } = this.props;
        const [variable] = sequencedVariables;

        const { value: choiceValue } = choice;
        const rankingValue = this.getRankingValue(value);
        const ranking: NumericRankingModel = {
            choiceValue,
            rankingValue,
        };

        this.setState((prevState) => {
            const newState = cloneDeep(prevState);
            remove(newState.rankings, (r) => r.choiceValue === choiceValue);
            if (rankingValue) {
                newState.rankings.push(ranking);
            }
            this.setNumericRankingMentions(variable, newState.rankings);
            return newState;
        });
    };

    setNumericRankingMentions(variable: VariableModel, rankings: NumericRankingModel[]): void {
        const { name: variableName } = variable;
        const rankingsToSet = this.getRankingIndicesToSet(rankings);
        this.setMentions(this.props, variableName, (mentions) => {
            mentions.clear();
            rankingsToSet.forEach((index) => {
                const choices = rankings
                    .filter((ranking) => ranking.rankingValue === index)
                    .map((ranking) => ranking.choiceValue)
                    .sort();
                const [firstChoiceInRanking] = choices;
                if (firstChoiceInRanking) {
                    choices.forEach(() => mentions.add(firstChoiceInRanking));
                } else {
                    mentions.add("");
                }
            });
            return mentions;
        });
    }

    getRankingIndicesToSet(rankings: NumericRankingModel[]): number[] {
        if (!rankings?.length) {
            return [];
        }
        const maxAnswers = this.getMaxAnswers();
        const maxRankingInState = Math.max(...rankings.map((ranking) => ranking.rankingValue));
        const maxRanking = Math.min(maxAnswers, maxRankingInState);
        const rankingsToSet = [...Array(maxRanking)].map((_, i) => i + 1);
        return rankingsToSet;
    }

    onFocus = (event) => {
        const inputIsEmpty = isEmpty(event.target.value);
        const alreadyHasMaxAnswers = this.state.rankings.length >= this.getMaxAnswers();
        if (inputIsEmpty && alreadyHasMaxAnswers) {
            event.target.blur();
        }
    };

    validate(): ValidationResultModel {
        const { question, questionnaire, showSoftPrompt, sequencedVariables, useClientValidation } =
            this.props;
        const [variable] = sequencedVariables;
        const { choiceList } = variable;
        const { rankings } = this.state;
        const validation = new ValidationBuilder();

        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            return new ValidationBuilder().build();
        }

        const customError =
            getSettingString(question, "CustomErrorMessage") ||
            this.parentCustomError(question.parent);
        const incompleteAnswerError = customError || "IncompleteAnswer";

        const hasValues = rankings.length;
        const isRequired = this.isAnswerRequired(questionnaire, question);
        if (!isRequired && !hasValues) {
            return validation.build();
        }

        const isSoftPrompt = this.isSoftPrompt(this.props);
        const softPromptCode = getSoftPromptCode(question);
        const hasNonSoftPromptAnswers = rankings.some(
            (ranking) => softPromptCode === undefined || ranking.choiceValue !== softPromptCode,
        );
        if (isSoftPrompt && !hasNonSoftPromptAnswers) {
            if (showSoftPrompt) {
                // Short-circuit validation: set invisible choice mentions (if any) and return valid
                const softPromptChoice = getChoice(variable, softPromptCode);
                if (softPromptChoice !== undefined && !isVisible(softPromptChoice)) {
                    const ranking = this.getSoftPromptChoiceRanking(softPromptChoice.value);
                    this.setMentions(this.props, variable.name, (mentions) =>
                        mentions.set(ranking.choiceValue),
                    );
                }
                return validation.build();
            }
            return this.validateSoftPrompt(this.props);
        }

        if (!hasValues) {
            return validation.fail(variable.name, incompleteAnswerError).build();
        }

        // Sorting numbers will return unexpected results without a comparer: (a, b) => a - b
        const values = rankings.map((r) => r.rankingValue).sort((a, b) => a - b);

        const hasFewerAnswersThanMinimum = values.length < this.getMinAnswers();
        if (hasFewerAnswersThanMinimum) {
            return validation
                .fail(variable.name, "InvalidChoicesMin", { min: String(this.getMinAnswers()) })
                .build();
        }

        const hasGapsInRanking = values.slice(1).some((value, i) => value - values[i] !== 1);
        const maxRankExceedsMaxLength = values.length !== max(values);
        const hasInvalidValue = values.some(
            (value, index) =>
                Number.isNaN(value) ||
                value < 0 ||
                value > choiceList.length ||
                values.indexOf(value) !== index,
        );

        const [firstValue] = values;
        const hasInvalidResponse =
            firstValue !== 1 || hasGapsInRanking || maxRankExceedsMaxLength || hasInvalidValue;
        if (hasInvalidResponse) {
            return validation
                .fail(variable.name, "InvalidRankingValue", {
                    min: String(1),
                    max: String(this.getMaxAnswers()),
                })
                .build();
        }

        return validation.build();
    }

    renderQuestion(question: QuestionModel) {
        const { language, sequencedVariables } = this.props;
        return (
            <NumericRankingRenderer
                {...getRendererProps(this.props)}
                labelsHeader={translate(question, "LabelsHeader", language)}
                answerHeader={translate(question, "AnswersHeader", language)}
                choices={sequencedVariables[0].choiceList}
                onChange={this.onChange}
                onFocus={this.onFocus}
                rankings={this.state.rankings}
                questionAriaDescribedBy={getNumericRankingQuestionAriaDescribedBy(
                    question,
                    language,
                )}
            />
        );
    }
}
